import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, X, CreditCard, Zap, Mail, ChevronRight, Rocket } from 'lucide-react';
import { PAYMENT_LINKS } from '../lib/stripe';

// Single tier definition
const BASIC_PLAN = {
  id: 'basic',
  name: 'Basic',
  description: 'For students looking to start their research journey',
  price: 9.99,
  featured: true,
  buttonText: 'Subscribe',
  icon: Zap,
  paymentLink: PAYMENT_LINKS?.BASIC,
  features: [
    { name: '20 professor searches per week', included: true },
    { name: 'Standard email generation', included: true },
    { name: 'Track up to 25 professors', included: true },
    { name: 'Community access', included: true },
    { name: 'Standard templates', included: true },
    { name: 'Email analytics', included: true },
    { name: 'AI email response analysis', included: true },
    { name: 'Research profile builder', included: true },
  ]
};

export function PricingPage() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annually'>('monthly');

  const handleSubscribe = () => {
    setLoading(true);
    const paymentLink = BASIC_PLAN.paymentLink;
    if (paymentLink) {
      //window.location.href = paymentLink;
      navigate('/signup')
    } else {
      navigate('/signup');
    }
  };

  // Calculate annual price (20% discount)
  const getPrice = (monthlyPrice: number) => {
    if (billingInterval === 'annually') {
      return (monthlyPrice * 12 * 0.8).toFixed(2); // 20% discount
    }
    return monthlyPrice.toFixed(2);
  };

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6 } }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({ 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.5, 
        delay: i * 0.1 
      } 
    })
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const Icon = BASIC_PLAN.icon;

  return (
    <div className="bg-gray-900 text-white min-h-screen">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-900/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-purple-900/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-1/2 w-64 h-64 bg-blue-900/20 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative z-10 py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1 
              className="text-4xl font-extrabold sm:text-5xl tracking-tight"
              variants={fadeInUp}
              custom={0}
            >
              <span className="block bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400">
                Simple, Transparent Pricing
              </span>
            </motion.h1>
            <motion.p 
              className="mt-5 max-w-xl mx-auto text-xl text-gray-300"
              variants={fadeInUp}
              custom={1}
            >
              Everything you need to accelerate your research journey
            </motion.p>

            {/* Billing toggle */}
            <motion.div 
              className="mt-12 flex justify-center"
              variants={fadeInUp}
              custom={2}
            >
              <div className="relative p-1 rounded-lg flex bg-gray-800 border border-gray-700 shadow-inner">
                <button
                  onClick={() => setBillingInterval('monthly')}
                  className={`relative w-32 rounded-md py-2 text-sm font-medium transition-colors ${
                    billingInterval === 'monthly' 
                      ? 'bg-indigo-700 text-white shadow-md' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <div className="relative">
                  <button
                    onClick={() => setBillingInterval('annually')}
                    className={`relative w-32 rounded-md py-2 text-sm font-medium transition-colors ${
                      billingInterval === 'annually' 
                        ? 'bg-indigo-700 text-white shadow-md' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Annually
                  </button>
                  {/* Save badge */}
                  <div className="absolute -top-3 -right-3">
                    <div className="relative inline-flex">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-20"></span>
                      <span className="relative inline-flex rounded-full px-2 py-0.5 text-xs font-semibold leading-4 text-white bg-indigo-600">
                        Save 20%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Single pricing tier display */}
          <motion.div 
            className="mt-16 max-w-lg mx-auto"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div
              className="relative rounded-2xl overflow-hidden border-2 border-indigo-500 bg-gray-800 backdrop-blur-sm shadow-xl shadow-indigo-900/20"
              variants={fadeInUp}
              custom={0}
              whileHover={{ 
                y: -5, 
                boxShadow: '0 20px 25px -5px rgba(79, 70, 229, 0.1), 0 10px 10px -5px rgba(79, 70, 229, 0.04)', 
                transition: { duration: 0.2 } 
              }}
            >
              <div className="px-8 pt-6 pb-6">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-lg bg-indigo-900/50 border border-indigo-800 flex items-center justify-center">
                    <Icon className="h-6 w-6 text-indigo-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">
                    {BASIC_PLAN.name} Plan
                  </h3>
                </div>

                <p className="mt-3 text-gray-400">
                  {BASIC_PLAN.description}
                </p>

                <div className="mt-6 flex items-baseline">
                  <span className="text-gray-300 text-2xl">$</span>
                  <span className="text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
                    {getPrice(BASIC_PLAN.price)}
                  </span>
                  <span className="ml-2 text-gray-400 text-lg">
                    /{billingInterval === 'monthly' ? 'month' : 'year'}
                  </span>
                </div>

                <motion.button
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="mt-8 w-full flex items-center justify-center px-4 py-3 rounded-lg text-base font-medium bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white shadow-lg shadow-indigo-900/20"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <CreditCard className="mr-2 h-5 w-5" />
                  {BASIC_PLAN.buttonText}
                </motion.button>
              </div>

              <div className="px-8 py-6 bg-gray-900/60 border-t border-gray-700">
                <h4 className="font-medium text-white mb-4">Everything you need:</h4>
                <ul className="space-y-4">
                  {BASIC_PLAN.features.map((feature, featureIndex) => (
                    <motion.li 
                      key={featureIndex} 
                      className="flex items-start"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 + (featureIndex * 0.05) }}
                    >
                      <Check className="h-5 w-5 text-indigo-400 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-300">
                        <span className="font-medium text-white">{feature.name.split(' ').slice(0, 2).join(' ')}</span>
                        {' ' + feature.name.split(' ').slice(2).join(' ')}
                      </span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="relative z-10 py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto">
              Everything you need to know about our pricing plan
            </p>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-2">
            {[
              {
                question: "What's included in the Basic Plan?",
                answer: "The Basic Plan includes 20 professor searches per week, AI-powered email generation, tracking for up to 25 professors, community access, standard templates, and email analytics."
              },
              {
                question: "Can I cancel my subscription?",
                answer: "Yes, you can cancel your subscription at any time. After cancellation, you'll maintain access until the end of your billing period."
              },
              {
                question: "Is there a student discount?",
                answer: "Our pricing is already optimized for students! We believe in making research connections accessible to all students."
              },
              {
                question: "What payment methods do you accept?",
                answer: "We accept all major credit cards including Visa, Mastercard, and American Express. All payments are processed securely through Stripe."
              },
              {
                question: "Do you offer refunds?",
                answer: "We offer a 7-day money-back guarantee. If you're not satisfied, contact our support team within 7 days of purchase."
              },
              {
                question: "What happens when I reach my weekly limit?",
                answer: "You can still access your existing connections, but you'll need to wait until your limit resets the following week to search for new professors."
              }
            ].map((faq, index) => (
              <motion.div 
                key={index}
                className="bg-gray-900/70 p-6 rounded-xl border border-gray-700 backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <h3 className="font-semibold text-white text-lg mb-3">{faq.question}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative z-10 py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="bg-gradient-to-r from-indigo-800 to-purple-800 rounded-2xl overflow-hidden relative"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden mix-blend-overlay opacity-10">
              <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full filter blur-3xl"></div>
              <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white rounded-full filter blur-3xl"></div>
            </div>
            
            <div className="px-8 py-16 md:px-12 md:py-20 text-center relative">
              <Rocket className="h-12 w-12 text-white/80 mx-auto mb-6" />
              <motion.h2 
                className="text-3xl font-bold text-white"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                Ready to Start Your Research Journey?
              </motion.h2>
              <motion.p 
                className="mt-4 max-w-2xl mx-auto text-lg text-indigo-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                Join thousands of students who've found research opportunities with Research-X
              </motion.p>
              <motion.div 
                className="mt-10 flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <motion.button
                  onClick={handleSubscribe}
                  className="inline-flex items-center justify-center px-8 py-3 rounded-lg text-base font-medium shadow-xl bg-white text-indigo-700 hover:bg-gray-100"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get Started Now
                  <ChevronRight className="ml-2 h-5 w-5" />
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Money-back guarantee section */}
      <div className="relative z-10 py-10 bg-gray-900 text-center">
        <motion.div
          className="flex items-center justify-center space-x-2 text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <Zap className="h-5 w-5 text-indigo-400" />
          <span className="text-sm">7-day money-back guarantee</span>
        </motion.div>
      </div>
    </div>
  );
}